import React, { useEffect } from 'react'
import { SurfacePalette, radius, type as typo } from '../tokens'

/**
 * Shared, palette-aware modal chrome for packet dialogs (overlay + centered card
 * + header with close). Using the surface palette keeps the same dialog looking
 * right on both the light Edit surface and the dark Present deck. Escape-to-close
 * applies to every packet dialog; in Present the host already suppresses deck
 * Arrow/Esc nav while a modal is open, so this Escape handler is the only one
 * that fires. Backdrop-click-to-close is opt-in (`closeOnBackdropClick`) so it
 * doesn't discard a half-filled form modal (e.g. the approval dialog) on a stray
 * click; the image-only preview opts in.
 */
export function PacketModalShell({
  palette,
  title,
  subtitle,
  headerActions,
  onClose,
  children,
  maxWidth = 440,
  padding = 24,
  hideHeader = false,
  closeOnBackdropClick = false,
}: {
  palette: SurfacePalette
  /** Optional — when empty the header collapses to just `headerActions` + close. */
  title?: string
  subtitle?: React.ReactNode
  /** Action controls rendered in the header row, left of the close button. */
  headerActions?: React.ReactNode
  onClose: () => void
  children: React.ReactNode
  maxWidth?: number
  /** Card padding. Lower it for media-dense dialogs that need vertical room. */
  padding?: number
  /**
   * Suppress the header row entirely (title + close). The caller owns its own
   * close affordance — used by the preview dialog, which puts close on a side
   * rail to give the ad the full modal height.
   */
  hideHeader?: boolean
  /**
   * Close when the user clicks the backdrop. Off by default so form dialogs don't
   * lose typed input to a stray click; the preview dialog opts in.
   */
  closeOnBackdropClick?: boolean
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  return (
    <div
      onClick={closeOnBackdropClick ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.55)',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: palette.surface,
          color: palette.text,
          border: `1px solid ${palette.hairline}`,
          borderRadius: radius.lg,
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.45)',
          width: '100%',
          maxWidth,
          padding,
          // Never taller than the viewport (minus the overlay's 16px inset top+bottom);
          // scroll internally instead of overflowing off-screen where the top is
          // unreachable. Short dialogs are unaffected.
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: hideHeader ? 0 : 20,
          fontFamily: typo.family.sans,
        }}
      >
        {!hideHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            {title || subtitle ? (
              <div>
                {title && (
                  <h2
                    style={{
                      margin: 0,
                      fontSize: typo.size.h3,
                      fontWeight: typo.weight.semibold,
                      color: palette.text,
                    }}
                  >
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: typo.size.eyebrow,
                      color: palette.textMuted,
                      lineHeight: 1.5,
                    }}
                  >
                    {subtitle}
                  </p>
                )}
              </div>
            ) : (
              <div style={{ minWidth: 0, flex: 1 }}>{headerActions}</div>
            )}
            {title && headerActions && <div>{headerActions}</div>}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                color: palette.textFaint,
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

/** Palette-aware text input used inside packet modals. */
export const modalInputStyle = (palette: SurfacePalette): React.CSSProperties => ({
  background: palette.bg,
  border: `1px solid ${palette.hairline}`,
  borderRadius: radius.sm,
  color: palette.text,
  padding: '8px 12px',
  fontSize: typo.size.body,
  fontFamily: typo.family.sans,
  width: '100%',
})

/** Primary (accent) action button for packet modals. */
export const modalPrimaryButtonStyle = (palette: SurfacePalette): React.CSSProperties => ({
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  background: palette.accent,
  color: '#fff',
  border: `1px solid ${palette.accent}`,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontSize: typo.size.body,
  fontWeight: typo.weight.semibold,
  cursor: 'pointer',
})

/** Secondary (ghost) action button for packet modals. */
export const modalGhostButtonStyle = (palette: SurfacePalette): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: palette.textMuted,
  border: `1px solid ${palette.hairline}`,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontSize: typo.size.body,
  fontWeight: typo.weight.medium,
  cursor: 'pointer',
})
